import type { TimerPreset, UserSummary } from '@susdle/shared';

export interface LobbyPlayer extends UserSummary {
  /** True if this avatar URL is one of Discord's flat default avatars (cdn.discordapp.com/embed/avatars/N.png). */
  isDefaultAvatar: boolean;
  /**
   * For default-avatar players: the Discord default index 0..5 from the user-id
   * pomelo formula or legacy discriminator mod 5. Used to deterministically
   * pick a chapbook-tinted background tone for the woodcut sigil so seats are
   * still distinguishable when players have no custom avatar.
   * Always a number for default avatars; undefined for custom ones.
   */
  defaultAvatarIndex?: 0 | 1 | 2 | 3 | 4 | 5;
}

export interface LobbySettings {
  foolEnabled: boolean;
  timerPreset: TimerPreset;
}

export interface LobbyView {
  /** All seated players in join order. */
  players: LobbyPlayer[];
  /** ID of the host. Always one of `players` while lobby is live. */
  hostId: string;
  /** ID of the local viewer. Determines whether host controls render and whether the kick UI appears on a row. */
  meId: string;
  settings: LobbySettings;
  /** Edge surface. Matters for staging only the dialog/banner; the lobby underneath reflects the snapshot from before the event. */
  systemMessage?: string;
}

export interface RoleCounts {
  ghosts: number;
  villagers: number;
  fools: number;
}

/** Per brief §6, mirrored from server-side role-distribution code. */
export function computeRoleDistribution(
  playerCount: number,
  foolEnabled: boolean,
): RoleCounts {
  const ghosts = Math.floor(playerCount / 3);
  const initialVillagers = playerCount - ghosts;
  const fools = foolEnabled ? Math.floor(initialVillagers / 3) : 0;
  const villagers = initialVillagers - fools;
  return { ghosts, villagers, fools };
}
